import React, { useEffect, useState } from "react";
import {
  Button,
  Typography,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Paper,
  Stack,
  Checkbox,
  Grid,
} from "@mui/material";
import CustomDialog from "global/components/CustomDialog/CustomDialog";
import {
  handleErrorMessage,
  isTruthy,
  openSuccessNotification,
} from "helpers/methods";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import {
  multipleSubscribeContacts,
  unsubscribeMultiContactsById,
} from "./ViewContact/ViewContactService";

interface EmailSubscriptionDialogProps {
  isDialogOpen: boolean;
  handleDialogClose: () => void;
  selectedContacts?: any;
  resetStates?: any;
}

const EmailSubscriptionDialog: React.FC<EmailSubscriptionDialogProps> = ({
  isDialogOpen,
  handleDialogClose,
  selectedContacts,
  resetStates,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>("");
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const validContacts =
    selectedContacts?.filter(
      (contact: any) => contact.status === 0 || contact.status === 1
    ) || [];

  const [selectedSubscribedContacts, setSelectedSubscribedContacts] = useState<
    number[]
  >(validContacts.filter((contact: any) => contact.status === 1) || []);
  const [selectedUnsubscribedContacts, setSelectedUnsubscribedContacts] =
    useState<number[]>(
      validContacts.filter((contact: any) => contact.status === 0) || []
    );

  useEffect(() => {
    if (isTruthy(selectedContacts)) {
      const filteredContacts = selectedContacts.filter(
        (contact: any) => contact.status === 0 || contact.status === 1
      );

      const subscribed = filteredContacts.filter(
        (contact: any) => contact.status === 1
      );
      const unsubscribed = filteredContacts.filter(
        (contact: any) => contact.status === 0
      );
      setSelectedSubscribedContacts(subscribed);
      setSelectedUnsubscribedContacts(unsubscribed);
    }
  }, [selectedContacts]);

  const handleUnsubscribe = async (
    contactData: { id: number; status: number }[]
  ) => {
    try {
      const ids = contactData
        .filter((contact) => contact.status === 1)
        .map((contact) => contact.id);

      if (ids.length === 0) return;
      const res = await unsubscribeMultiContactsById(ids);
      openSuccessNotification(res.message);
    } catch (error: any) {
      throw error;
    }
  };

  const handleSubscribe = async (
    contactData: { id: number; status: number }[]
  ) => {
    try {
      const ids = contactData
        .filter((contact) => contact.status === 0)
        .map((contact) => contact.id);

      if (ids.length === 0) return;
      const res = await multipleSubscribeContacts(ids);
      openSuccessNotification(res.message);
    } catch (error: any) {
      throw error;
    }
  };

  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSubscriptionStatus(event.target.value);
  };

  const handleCheckboxChange = (status: string, checked: boolean) => {
    setSelectedStatuses((prev) => {
      if (checked) {
        return prev.includes(status) ? prev : [...prev, status];
      } else {
        return prev.filter((s) => s !== status);
      }
    });
  };

  const handleSubmit = async () => {
    const hasMixedStatuses =
      (selectedSubscribedContacts.length ?? 0) > 0 &&
      (selectedUnsubscribedContacts.length ?? 0) > 0;
    const statusToCheck = hasMixedStatuses
      ? selectedStatuses
      : [subscriptionStatus];

    if (
      statusToCheck.length === 0 ||
      (statusToCheck.length === 1 && !statusToCheck[0])
    ) {
      return;
    }

    try {
      setLoading(true);
      if (hasMixedStatuses) {
        if (selectedStatuses.includes("subscribed")) {
          const unsubscribedContactsToUpdate = selectedContacts.filter(
            (contact: any) => contact.status === 0
          );
          await handleSubscribe(unsubscribedContactsToUpdate);
        }
        if (selectedStatuses.includes("unsubscribed")) {
          const subscribedContactsToUpdate = selectedContacts.filter(
            (contact: any) => contact.status === 1
          );
          if (subscribedContactsToUpdate.length > 0) {
            await handleUnsubscribe(subscribedContactsToUpdate);
          }
        }
      } else {
        if (subscriptionStatus === "subscribed") {
          const unsubscribedContactsToUpdate = selectedContacts.filter(
            (contact: any) => contact.status === 0
          );
          await handleSubscribe(unsubscribedContactsToUpdate);
        } else if (subscriptionStatus === "unsubscribed") {
          const subscribedContactsToUpdate = selectedContacts.filter(
            (contact: any) => contact.status === 1
          );

          if (subscribedContactsToUpdate.length > 0) {
            await handleUnsubscribe(subscribedContactsToUpdate);
          }
        }
      }
      resetStates();
      handleDialogClose();
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleCancel = () => {
    setSubscriptionStatus("");
    setSelectedStatuses([]);
    handleDialogClose();
  };

  const statusExplanations = [
    {
      status: "Subscribed",
      color: "#3cad44",
      description:
        "Contacts who have opted in to receive your email marketing.",
    },
    {
      status: "Unsubscribed",
      color: "#5a5f73",
      description:
        "Contacts who previously received your email marketing but chose to opt out.",
    },
    {
      status: "Bounced",
      color: "#d72727",
      description: "Contacts whose emails have been hard bounced.",
    },
    {
      status: "Spam",
      color: "#ff7c2e",
      description: "Contacts who marked your emails as spam.",
    },
    {
      status: "Archived",
      color: "#9f9fad",
      description: "Contacts who have been deleted from your list.",
    },
  ];

  const getBody = () => {
    const subscribedContacts = selectedSubscribedContacts.length ?? 0;
    const unsubscribedContacts = selectedUnsubscribedContacts.length ?? 0;
    const hasMixedStatuses = subscribedContacts > 0 && unsubscribedContacts > 0;
    const totalValidContacts = validContacts.length;
    const totalSelectedContacts = selectedContacts?.length || 0;
    const ignoredContacts = totalSelectedContacts - totalValidContacts;

    return (
      <Stack pt={2}>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={5}>
            <Paper
              elevation={0}
              sx={{
                bgcolor: "#f8f9fa",
                p: { xs: 2, sm: 3 },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  fontSize: { xs: "1.1rem", sm: "1.25rem" },
                }}
              >
                Email statuses explained
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {statusExplanations.map((item, index) => (
                  <Box key={index}>
                    <Chip
                      label={item.status}
                      size="small"
                      sx={{
                        backgroundColor: item.color,
                        color: "white",
                        mb: 0.5,
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={7}>
            <Box>
              {ignoredContacts > 0 && (
                <Box sx={{ mb: 2, p: 2, bgcolor: "#fff3cd", borderRadius: 1 }}>
                  <Typography variant="body2" color="#856404">
                    Note: {ignoredContacts} contact
                    {ignoredContacts !== 1 ? "s" : ""} with invalid subscription
                    status
                    {ignoredContacts !== 1 ? "es" : ""} will be ignored.
                  </Typography>
                </Box>
              )}
              {totalValidContacts > 0 && (
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                  }}
                >
                  You've selected <strong>{totalValidContacts} contacts</strong>{" "}
                  with the following email subscription statuses:
                </Typography>
              )}
              <Box sx={{ mb: 3 }}>
                {subscribedContacts > 0 && (
                  <Typography
                    variant="body2"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    • <strong>{subscribedContacts} subscribed</strong>
                  </Typography>
                )}
                {unsubscribedContacts > 0 && (
                  <Typography
                    variant="body2"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    • <strong>{unsubscribedContacts} unsubscribed</strong>
                  </Typography>
                )}
              </Box>

              {totalValidContacts > 0 && (
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Edit subscription status to:
                </Typography>
              )}

              {hasMixedStatuses ? (
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedStatuses.includes("subscribed")}
                        onChange={(e) => {
                          handleCheckboxChange("subscribed", e.target.checked);
                        }}
                      />
                    }
                    label={
                      <Box sx={{ ml: { xs: 0, sm: 1 } }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                          }}
                        >
                          Subscribed{" "}
                          <Typography component="span" color="text.secondary">
                            ({unsubscribedContacts} contact
                            {unsubscribedContacts !== 1 ? "s" : ""} eligible to
                            be updated)
                          </Typography>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Contacts must have given explicit consent for you to
                          send them marketing emails. Resubscribing contacts can
                          take up to 5 hours.
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: "flex-start", mb: 2 }}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedStatuses.includes("unsubscribed")}
                        onChange={(e) => {
                          handleCheckboxChange(
                            "unsubscribed",
                            e.target.checked
                          );
                        }}
                      />
                    }
                    label={
                      <Box sx={{ ml: { xs: 0, sm: 1 } }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                          }}
                        >
                          Unsubscribed{" "}
                          <Typography component="span" color="text.secondary">
                            ({subscribedContacts} contact
                            {subscribedContacts !== 1 ? "s" : ""} eligible to be
                            updated)
                          </Typography>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Marked as unsubscribed, these contacts won't receive
                          your marketing emails but will remain in your contact
                          list.
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: "flex-start", mb: 1 }}
                  />
                </Box>
              ) : (
                <RadioGroup
                  value={subscriptionStatus}
                  onChange={handleStatusChange}
                >
                  {unsubscribedContacts > 0 && (
                    <FormControlLabel
                      value="subscribed"
                      control={<Radio size="small" />}
                      label={
                        <Box sx={{ ml: { xs: 0, sm: 1 } }}>
                          <Typography variant="body1">
                            Subscribed{" "}
                            <Typography component="span" color="text.secondary">
                              ({totalValidContacts} contact
                              {totalValidContacts !== 1 ? "s" : ""} eligible to
                              be updated)
                            </Typography>
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Contacts must have given explicit consent for you to
                            send them marketing emails. Resubscribing contacts
                            can take up to 5 hours.
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: "flex-start", mb: 2 }}
                    />
                  )}

                  {subscribedContacts > 0 && (
                    <FormControlLabel
                      value="unsubscribed"
                      control={<Radio size="small" />}
                      label={
                        <Box sx={{ ml: { xs: 0, sm: 1 } }}>
                          <Typography variant="body1">
                            Unsubscribed{" "}
                            <Typography component="span" color="text.secondary">
                              ({totalValidContacts} contact
                              {totalValidContacts !== 1 ? "s" : ""} eligible to
                              be updated)
                            </Typography>
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Marked as unsubscribed, these contacts won't receive
                            your marketing emails but will remain in your
                            contact list.
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: "flex-start", mb: 1 }}
                    />
                  )}
                </RadioGroup>
              )}
            </Box>
          </Grid>
        </Grid>
      </Stack>
    );
  };

  const getFooter = () => {
    return (
      <Stack direction="row" spacing={1}>
        <Button variant="outlined" size="small" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleSubmit}
          disabled={!subscriptionStatus && selectedStatuses.length === 0}
        >
          Submit
        </Button>
      </Stack>
    );
  };

  return (
    <>
      <CustomDialog
        isDialogOpen={isDialogOpen}
        handleDialogClose={handleDialogClose}
        title={
          <Typography variant="h4" color="#7551D0">
            Edit email subscription status
          </Typography>
        }
        body={getBody()}
        footer={getFooter()}
        maxWidth="md"
        closeButtonVisibility
        closable
        persistOnClickOutside
        persistOnEsc
      />
      <CustomLoader isLoading={loading} />
    </>
  );
};

export default EmailSubscriptionDialog;
